import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import Box from '@mui/material/Box';
import { Text } from '../../../Atoms/Text';

const BOX_CLASS = {
  display: 'flex',
  flexDirection: 'column',
  gap: '.3rem'
};

type FinancialInputFieldProps = {
  value: number | '';
  label: string;
  disabled?: boolean;
  onChange?: (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string;
  name: string;
  symbol?: string;
  positionSymbol?: 'start' | 'end';
  allowNegative?: boolean;
};

const FinancialInputField = ({
  value,
  label,
  disabled,
  onChange,
  error,
  helperText,
  name,
  symbol = '$',
  positionSymbol = 'start',
  allowNegative = false
}: FinancialInputFieldProps) => {
  const [displayValue, setDisplayValue] = useState(value !== '' ? value.toFixed(2) : '');

  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDisplayValue(value !== '' ? value.toFixed(2) : '');
  }, [value]);

  function cleanInput(input: string): string {
    const negative = allowNegative && input.trim().startsWith('-');
    let cleaned = input.replace(/[^0-9.]/g, '');

    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    if (cleaned.includes('.')) {
      const [whole, decimal] = cleaned.split('.');
      cleaned = `${whole || '0'}.${decimal.slice(0, 2)}`;
    }

    if (negative) {
      cleaned = `-${cleaned}`;
    }

    return cleaned;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFocused(true);
    const input = e.target.value;

    const cleaned = cleanInput(input);
    if (cleaned === '' && input !== '') return;
    setDisplayValue(cleaned);

    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: cleaned
        }
      };
      onChange(name)(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    if (displayValue !== '') {
      const parsed = parseFloat(displayValue);
      if (!isNaN(parsed)) {
        const fixed = parsed.toFixed(2);
        setDisplayValue(fixed);
      } else {
        setDisplayValue(value !== '' ? value.toFixed(2) : '');
      }
    }
  };

  return (
    <Box
      sx={BOX_CLASS}
      data-error-field={error ? name : undefined}>
      <Text
        dataTestId={`${name}-label-field`}
        size="sm"
        color="grey6">
        {label}
      </Text>
      <TextField
        type="text"
        size="small"
        id={`financial-${name}`}
        name={name}
        data-testid={`${name}-input`}
        value={displayValue}
        onChange={disabled ? undefined : handleInputChange}
        onBlur={handleBlur}
        fullWidth
        disabled={!!disabled}
        helperText={helperText}
        error={!!error}
        slotProps={{
          formHelperText: error ? { role: 'alert' } : undefined,
          input: {
            [`${positionSymbol}Adornment`]: <InputAdornment position={positionSymbol}>{symbol}</InputAdornment>
          }
        }}
      />
    </Box>
  );
};

export default FinancialInputField;
